import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react'

// =============================================================================
//  Wskaźnik stanu węzła — kropka z pierścieniem odliczającym do kolejnego przebiegu.
//
//  Dostępność węzła definiuje integracja PRODUKCJI: chodzi najczęściej ze wszystkich,
//  więc jej przebiegi są najlepszym dowodem, że węzeł żyje.
//
//  Decyzje projektowe (świadome, po odrzuceniu bogatszych wariantów):
//   * BEZ obracającego się łuku podczas pracy — ruch w pasku, na który patrzy się cały
//     dzień, męczy. Faza pracy jest sygnalizowana samą zmianą koloru kropki: zmiana barwy
//     jest przeduwagowa, więc zauważysz ją kątem oka, ale nie ciągnie wzroku jak animacja.
//   * Błysk (rozchodzący się pierścień) tylko po ZAOBSERWOWANEJ zmianie znacznika udanego
//     połączenia, nie na stanie chwilowym — przebieg bywa krótszy niż okres odpytywania,
//     więc łapanie „trwa teraz" w locie i tak by go przegapiło.
//   * Pierścień normalizowany do BIEŻĄCEGO oczekiwania. Przy błędach backoff wydłuża
//     odstęp, więc stała skala sprawiałaby wrażenie, że wskaźnik zamarł.
//   * Zegar serwera przychodzi w tej samej odpowiedzi co dane. Odliczanie liczone lokalnym
//     zegarem rozjeżdżałoby się przy przesunięciu czasu na stacji i wyglądało na błąd systemu.
// =============================================================================

export type StanIntegracji = {
  przebiegStart: string | null
  przebiegKoniec: string | null
  wynik: string | null
  log: string | null
  nastepne: string | null
  interwal: number
  bledy: number
  // Różnica zegarów przeglądarki i serwera w ms, policzona RAZ w chwili odebrania odpowiedzi.
  // Poprzednio leżał tu surowy `serwer_teraz`, co miało dwie wady: wartość zmieniała się
  // przy KAŻDYM pobraniu (więc porównanie stanu zawsze wykrywało zmianę), a przy dłuższym
  // trzymaniu w pamięci robiła się nieaktualna i przesuwała odliczanie. Przesunięcie zegarów
  // jest stałe, więc stan potrafi stać w miejscu i pozostaje poprawne dowolnie długo.
  przesuniecieMs: number
  ostatnieUdane: string | null
}

// ---------------------------------------------------------------------------
//  Stan wskaźników trzymany POZA drzewem Reacta.
//
//  Gdyby leżał w stanie CardZamowieniaWidget, odświeżanie co 15 s przerysowywałoby cały
//  widget zamówień — tabelę i (przy otwartym oknie) mapę Google. Wskaźnik ma 22 px i nie
//  ma prawa pociągać za sobą takiego kosztu. Tutaj zapis powiadamia wyłącznie zamontowane
//  wskaźniki, a gdy dane się nie zmieniły, nie powiadamia nikogo.
// ---------------------------------------------------------------------------

type MapaStanu = Record<number, StanIntegracji>

let mapaStanu: MapaStanu = {}
let odciskStanu = ''
const subskrybenci = new Set<() => void>()

export function zapiszStanWezlow(mapa: MapaStanu): void {
  const odcisk = JSON.stringify(mapa)
  if (odcisk === odciskStanu) return
  odciskStanu = odcisk
  mapaStanu = mapa
  subskrybenci.forEach((powiadom) => powiadom())
}

const subskrybuj = (powiadom: () => void) => {
  subskrybenci.add(powiadom)
  return () => { subskrybenci.delete(powiadom) }
}
const migawka = () => mapaStanu

const KOLOR = {
  ok: '#2e7d32',
  pracuje: '#1565c0',
  blad: '#d32f2f',
  brak: '#9e9e9e',
}

const czasPl = (v: string | null) => (v ? new Date(v).toLocaleString('pl-PL') : 'nigdy')

// Klatki kluczowe wstrzykiwane raz na moduł, nie na komponent — wskaźników jest tylu, ilu
// węzłów, a każdy własny <style> to kolejna reguła w arkuszu przy każdym przerysowaniu.
const KLATKI_ID = 'wskaznik-blysk-klatki'
if (typeof document !== 'undefined' && !document.getElementById(KLATKI_ID)) {
  const el = document.createElement('style')
  el.id = KLATKI_ID
  el.textContent = '@keyframes wskaznik-blysk{from{transform:scale(.5);opacity:.9}to{transform:scale(1.9);opacity:0}}'
  document.head.appendChild(el)
}

export const WskaznikWezla: React.FC<{
  /** Wskaźnik sam czyta swój stan ze wspólnego magazynu — rodzic go nie przekazuje.
   *  Dzięki temu odświeżenie danych przerysowuje 22-pikselową kropkę, a nie cały widget. */
  wezelId: number
  tytul?: string
  rozmiar?: number
  // Aplikacja ma własny dymek zastępujący natywne `title` (czytelniejszy i pojawia się od
  // razu). Gdy rodzic poda te funkcje, wskaźnik oddaje mu gotowy opis zamiast rysować
  // natywną chmurkę; bez nich zostaje `title`, żeby komponent działał samodzielnie.
  onPokazOpis?: (opis: string, prostokat: DOMRect) => void
  onUkryjOpis?: () => void
}> = ({
  wezelId, tytul, rozmiar = 14, onPokazOpis, onUkryjOpis,
}) => {
  const mapa = useSyncExternalStore(subskrybuj, migawka, migawka)
  const stan: StanIntegracji | null = mapa[wezelId] ?? null

  const [, przerysuj] = useState(0)
  const blyskDo = useRef<number>(0)
  const poprzednieUdane = useRef<string | null>(null)

  // Przerysowanie co sekundę wystarcza: pierścień porusza się wolno, a częstsze odświeżanie
  // to tylko obciążenie przeglądarki przy kilku węzłach naraz.
  useEffect(() => {
    const t = setInterval(() => przerysuj((x) => x + 1), 1000)
    return () => clearInterval(t)
  }, [])

  // Błysk odpalamy na ZMIANIE znacznika, nie na jego wartości — dzięki temu działa
  // niezależnie od tego, jak krótki był przebieg i jak rzadko odpytujemy API.
  useEffect(() => {
    const u = stan?.ostatnieUdane ?? null
    if (u && poprzednieUdane.current && u !== poprzednieUdane.current) {
      blyskDo.current = Date.now() + 700
    }
    poprzednieUdane.current = u
  }, [stan?.ostatnieUdane])

  // Grubość pierścienia skaluje się ze średnicą. Przy stałych 1.5 px większy wskaźnik
  // wyglądałby na cieniutką obrączkę — proporcja jest tym, co decyduje o czytelności.
  const grubosc = Math.max(1.5, rozmiar / 9)
  const r = rozmiar / 2 - grubosc
  const obwod = 2 * Math.PI * r

  const brakDanych = !stan || (!stan.nastepne && !stan.przebiegStart && !stan.ostatnieUdane)

  // Korekta różnicy zegarów: serwer podał swój czas przy tej samej odpowiedzi.
  const przesuniecie = stan?.przesuniecieMs ?? 0
  const teraz = Date.now() + przesuniecie

  const pracuje = !!stan?.przebiegStart && (!stan.przebiegKoniec || new Date(stan.przebiegStart) > new Date(stan.przebiegKoniec))
  const blad = stan?.wynik === 'blad' || stan?.wynik === 'przerwany'
  const blysk = Date.now() < blyskDo.current

  const kolor = pracuje ? KOLOR.pracuje : blad ? KOLOR.blad : stan?.wynik === 'ok' ? KOLOR.ok : KOLOR.brak

  // Ułamek pozostałego czasu. Skala to odstęp między KOŃCEM ostatniego przebiegu a terminem
  // następnego — czyli faktyczne bieżące oczekiwanie, także gdy backoff je wydłużył.
  let ulamek = 0
  if (stan?.nastepne) {
    const cel = new Date(stan.nastepne).getTime()
    const od = stan.przebiegKoniec ? new Date(stan.przebiegKoniec).getTime() : cel - Math.max(1, stan.interwal) * 1000
    const calosc = Math.max(1000, cel - od)
    ulamek = Math.max(0, Math.min(1, (cel - teraz) / calosc))
  }

  const zostalo = stan?.nastepne ? Math.max(0, Math.round((new Date(stan.nastepne).getTime() - teraz) / 1000)) : null
  const opis = brakDanych
    ? [tytul, 'Brak integracji produkcji dla tego węzła', 'Wskaźnik nie ma z czego czytać stanu — załóż integrację produkcji.'].filter(Boolean).join('\n')
    : [
        tytul,
        pracuje ? 'Trwa pobieranie danych' : blad ? `Brak połączenia (nieudanych prób: ${stan!.bledy})` : 'Połączony',
        `Ostatnie udane połączenie: ${czasPl(stan!.ostatnieUdane)}`,
        stan!.log ? `Ostatni przebieg: ${stan!.log}` : null,
        pracuje || zostalo == null ? null : blad ? `Ponowna próba za ${zostalo} s` : `Następne pobranie za ${zostalo} s`,
      ].filter(Boolean).join('\n')

  // Gdy rodzic obsługuje dymek sam, natywny `title` musi zniknąć — inaczej pokazałyby się dwa.
  const zdarzenia = onPokazOpis
    ? {
        onMouseEnter: (e: React.MouseEvent<HTMLSpanElement>) => onPokazOpis(opis, e.currentTarget.getBoundingClientRect()),
        onMouseLeave: () => onUkryjOpis?.(),
      }
    : {}
  const tytulNatywny = onPokazOpis ? undefined : opis

  if (brakDanych) {
    // Pusty, przerywany obrys zamiast wypełnionej kropki. „Nie mam o tym węźle żadnej
    // informacji" musi wyglądać inaczej niż jakikolwiek stan pracy — szara kropka była
    // nieodróżnialna od stanu i czytana jako awaria.
    return (
      <span {...zdarzenia} title={tytulNatywny} aria-hidden
        style={{ width: rozmiar, height: rozmiar, borderRadius: '50%', border: `${grubosc}px dashed ${KOLOR.brak}`, boxSizing: 'border-box', flexShrink: 0, display: 'inline-block' }} />
    )
  }

  return (
    <span {...zdarzenia} title={tytulNatywny} aria-hidden style={{ display: 'inline-flex', flexShrink: 0, position: 'relative', width: rozmiar, height: rozmiar }}>
      <svg width={rozmiar} height={rozmiar} viewBox={`0 0 ${rozmiar} ${rozmiar}`}>
        <circle cx={rozmiar / 2} cy={rozmiar / 2} r={r} fill="none" stroke="currentColor" strokeOpacity={0.18} strokeWidth={grubosc} />
        {ulamek > 0 && !pracuje && (
          <circle
            cx={rozmiar / 2} cy={rozmiar / 2} r={r} fill="none" stroke={kolor} strokeWidth={grubosc} strokeLinecap="round"
            transform={`rotate(-90 ${rozmiar / 2} ${rozmiar / 2})`}
            strokeDasharray={obwod} strokeDashoffset={obwod * (1 - ulamek)}
          />
        )}
        <circle cx={rozmiar / 2} cy={rozmiar / 2} r={r / 2.2} fill={kolor} />
        {blysk && (
          // CSS, nie SMIL. Poprzednio były tu elementy <animate>; przy działającej integracji
          // błysk odpala się co przebieg, a montowanie i usuwanie SMIL-a tysiące razy dziennie
          // to znany sposób na narastające zużycie pamięci w Chrome. CSS animuje kompozytor
          // i znika razem z elementem.
          <circle cx={rozmiar / 2} cy={rozmiar / 2} r={r} fill="none" stroke={KOLOR.ok} strokeWidth={grubosc}
            style={{ transformOrigin: 'center', animation: 'wskaznik-blysk 0.7s ease-out forwards' }} />
        )}
      </svg>
    </span>
  )
}
